import React, { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageBanner from "@/components/PageBanner";
import SectionTitle from "@/components/SectionTitle";
import FinalCTA from "@/components/FinalCTA";
import { IMAGES } from "@/lib/content";
import { Car, KeyRound, CheckCircle2, ArrowRight } from "lucide-react";

const CarSolutions = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [location]);

  const renderPoints = (points) => (
    <ul className="mt-6 space-y-3">
      {points.map((p) => (
        <li key={p} className="flex items-start gap-3 text-abayle-charcoal/80">
          <CheckCircle2 className="h-5 w-5 text-abayle-gold shrink-0 mt-0.5" />
          <span className="text-[15px]">{p}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="min-h-screen flex flex-col bg-abayle-ivory">
      <Navbar />
      <main>
        <PageBanner
          eyebrow="Car Solutions"
          title="Car Solutions"
          subtitle="Professional car sales and car rental services for business, project, and operational mobility."
          image={IMAGES.pickup}
        />

        {/* Car Sales */}
        <section id="sales" className="bg-white scroll-mt-32" data-testid="car-sales-section">
          <div className="container-abayle py-14 sm:py-20 lg:py-28 grid lg:grid-cols-2 gap-8 sm:gap-14 items-center">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-abayle-gold z-10" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-abayle-gold z-10" />
              <img src={IMAGES.carSales} alt="Car Sales" className="w-full h-[280px] sm:h-[420px] lg:h-[440px] object-cover rounded-sm shadow-xl" />
            </div>
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div className="h-14 w-14 rounded-md bg-abayle-green text-white flex items-center justify-center border-2 border-abayle-gold">
                  <Car className="h-6 w-6" />
                </div>
                <span className="text-abayle-gold uppercase text-xs tracking-[0.3em] font-bold">Car Sales</span>
              </div>
              <SectionTitle title="Car Sales for Business and Field Use" />
              <p className="text-abayle-charcoal/85 text-base lg:text-lg leading-relaxed">
                Abayle supports clients looking to acquire vehicles for company operations, field programmes, transport
                assignments, and project deployment. The focus is on reliability, practicality, and suitability for
                Somali conditions.
              </p>
              {renderPoints([
                "Operational vehicles for company use",
                "Cars for projects and field teams",
                "Flexible options for client needs",
                "Reliable mobility support",
              ])}
            </div>
          </div>
        </section>

        {/* Car Rental */}
        <section id="rental" className="bg-abayle-ivory scroll-mt-32" data-testid="car-rental-section">
          <div className="container-abayle py-14 sm:py-20 lg:py-28 grid lg:grid-cols-2 gap-8 sm:gap-14 items-center">
            <div className="lg:order-2 relative">
              <div className="absolute -top-4 -right-4 w-24 h-24 border-t-2 border-r-2 border-abayle-gold z-10" />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 border-b-2 border-l-2 border-abayle-gold z-10" />
              <img src={IMAGES.carRental} alt="Car Rental" className="w-full h-[280px] sm:h-[420px] lg:h-[440px] object-cover rounded-sm shadow-xl" />
            </div>
            <div className="lg:order-1">
              <div className="flex items-center gap-4 mb-4">
                <div className="h-14 w-14 rounded-md bg-abayle-green text-white flex items-center justify-center border-2 border-abayle-gold">
                  <KeyRound className="h-6 w-6" />
                </div>
                <span className="text-abayle-gold uppercase text-xs tracking-[0.3em] font-bold">Car Rental</span>
              </div>
              <SectionTitle title="Car Rental for Flexible Mobility" />
              <p className="text-abayle-charcoal/85 text-base lg:text-lg leading-relaxed">
                Abayle's car rental service is designed for organizations and clients that require dependable short
                term or ongoing transport support — for business travel, field activity, visiting teams, project
                implementation, and general mobility requirements.
              </p>
              {renderPoints([
                "Daily, weekly, or project based rental options",
                "Business and field mobility support",
                "Practical transport solutions",
                "Flexible arrangements for client operations",
              ])}
            </div>
          </div>
        </section>

        {/* Why Matter */}
        <section className="bg-abayle-green-dark text-white relative isolate overflow-hidden" data-testid="car-why-matter">
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 80%, #C9A13B 0, transparent 40%)" }} />
          <div className="container-abayle py-14 sm:py-20 lg:py-24 relative grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-2">
              <SectionTitle eyebrow="Why It Matters" title="Reliable Mobility Supports Better Operations" invert />
            </div>
            <div className="lg:col-span-3 text-white/85 text-base lg:text-lg leading-relaxed">
              <p>
                In Somalia, dependable mobility is essential for business continuity, field coordination, site visits,
                and operational efficiency. Abayle's car sales and car rental services help clients stay mobile,
                organized, and ready for movement across different working environments.
              </p>
              <Link to="/contact" className="btn-primary-abayle bg-abayle-gold border-abayle-gold text-abayle-green hover:bg-white hover:border-white mt-8" data-testid="car-cta-contact">
                Contact Us
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default CarSolutions;
